import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';

const SpreadsheetPreview = ({ url, filename }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSheet, setCurrentSheet] = useState(0);

  useEffect(() => {
    if (url) {
      fetchContentAndParse();
    } else {
      setError('No URL provided for preview');
      setLoading(false);
    }
  }, [url]);

  const fetchContentAndParse = async () => {
    setLoading(true);
    setError(null);
    setData(null); // Clear previous data when fetching new content

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const lowercasedFilename = filename.toLowerCase();
      const isCSV = lowercasedFilename.endsWith('.csv') || response.headers.get('content-type')?.includes('text/csv');
      const isExcel = lowercasedFilename.endsWith('.xlsx') || lowercasedFilename.endsWith('.xls');

      if (isCSV) {
        const fetchedContent = await response.text();
        parseCSV(fetchedContent);
      } else if (isExcel) {
        // Excel file parsing logic
        const arrayBufferContent = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBufferContent, { type: 'array' });
        const sheetNames = workbook.SheetNames;
        
        // You might want to get the data for the 'currentSheet' here
        // For simplicity, we'll parse the first sheet initially
        const worksheet = workbook.Sheets[sheetNames[currentSheet]]; 
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }); // Convert to array of arrays

        if (jsonData.length > 0) {
          const headers = jsonData[0].map(h => String(h || '').trim()); // Ensure headers are strings
          const rows = jsonData.slice(1).map(row => 
            // Ensure each row has enough cells to match headers and cells are strings
            headers.map((_, i) => String(row[i] || '').trim())
          );
          setData({
            sheets: sheetNames,
            currentSheetData: { headers, rows }
          });
        } else {
          throw new Error('Excel file is empty or could not be parsed.');
        }
      } else {
        // If not CSV and not Excel, then it's an unsupported type
        throw new Error('Unsupported file type for preview. Only CSV and Excel (.xlsx, .xls) files are supported.');
      }

    } catch (err) {
      setError('Failed to fetch or parse spreadsheet: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const parseCSV = (csvContent) => {
    try {
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        let i = 0;

        while (i < line.length) {
          const char = line[i];
          const nextChar = line[i + 1];

          if (char === '"') {
            if (inQuotes && nextChar === '"') {
              // Escaped quote: ""
              current += '"';
              i += 2;
            } else {
              // Toggle quote state
              inQuotes = !inQuotes;
              i++;
            }
          } else if (char === ',' && !inQuotes) {
            // Field separator outside of quotes
            result.push(current.trim());
            current = '';
            i++;
          } else {
            // Regular character
            current += char;
            i++;
          }
        }
        // Add the last field after the loop
        result.push(current.trim());
        return result;
      };

      const lines = csvContent.split('\n').filter(line => line.trim());
      if (lines.length === 0) {
        throw new Error('Empty CSV file');
      }

      const headers = parseCSVLine(lines[0]).map(h => h.replace(/^"|"$/g, ''));
      const rows = lines.slice(1)
        .map(line => parseCSVLine(line))
        .filter(row => row.some(cell => cell && cell.trim())) // Filter out entirely empty rows
        .map(row => {
          // Ensure rows have enough cells to match headers
          const normalizedRow = [...row];
          while (normalizedRow.length < headers.length) {
            normalizedRow.push('');
          }
          return normalizedRow.slice(0, headers.length).map(cell => cell.replace(/^"|"$/g, ''));
        });
      
      setData({
        sheets: ['CSV Data'], // CSV files typically have one "sheet"
        currentSheetData: { headers, rows }
      });
    } catch (err) {
      throw new Error('Failed to parse CSV: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-8 text-center min-h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg p-8 text-center min-h-96 flex items-center justify-center">
        <div className="text-red-500">
          <p className="font-medium">Failed to load spreadsheet</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center min-h-96 flex items-center justify-center">
        <div className="text-gray-500">
          <p className="text-lg">No data available</p>
        </div>
      </div>
    );
  }

  // Determine current sheet data based on currentSheet state for Excel files
  // For CSV, data.currentSheetData is already set, and sheets will only contain 'CSV Data'
  const displayData = data.sheets[0] === 'CSV Data' 
    ? data.currentSheetData 
    : (() => {
        // This part would ideally re-parse the correct sheet if currentSheet changes,
        // or the workbook object would be stored in state to quickly access other sheets.
        // For now, it will just show the first sheet loaded during initial parse.
        // To truly switch sheets, you'd need to re-read the worksheet based on currentSheet.
        // This is a more complex state management for multi-sheet Excel files.
        return data.currentSheetData; // Assumes currentSheetData is for the selected sheet
      })();


  return (
    <div className="bg-white rounded-lg overflow-hidden min-h-96">
      <div className="bg-gray-50 p-4 border-b flex justify-between items-center">
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-600">
            {(data.sheets && data.sheets[0] === 'CSV Data') ? 'CSV Preview' : 'Spreadsheet Preview'}
          </p>
          {data.sheets && data.sheets.length > 1 && (
            <select
              value={currentSheet}
              onChange={(e) => setCurrentSheet(parseInt(e.target.value))}
              className="text-xs border rounded px-2 py-1"
            >
              {data.sheets.map((sheet, idx) => (
                <option key={idx} value={idx}>{sheet}</option>
              ))}
            </select>
          )}
        </div>
        <p className="text-xs text-gray-500">
          {displayData?.rows?.length || 0} rows × {displayData?.headers?.length || 0} columns
        </p>
      </div>
      <div className="overflow-auto max-h-96">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {(displayData?.headers || []).map((header, idx) => (
                <th key={idx} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                  {header || `Column ${idx + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(displayData?.rows || []).map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx} className="px-4 py-2 text-sm text-gray-900 border-b whitespace-nowrap">
                    {cell || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SpreadsheetPreview;